import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Global error boundary. We render a calm, non-alarming fallback — older
 * adults already have plausible technological anxiety; a screaming red
 * "Something went wrong!" actively makes that worse.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary] caught', error, info);
  }

  override render(): ReactNode {
    if (this.state.error) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div className="status-block" role="alert">
          <div>
            <p className="status-block__title">We hit a small snag.</p>
            <p>Please try refreshing the page. Nothing was lost.</p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
